/* eslint-disable jsx-a11y/anchor-is-valid */

import * as Yup from 'yup'

import { SignInButton, useAuthProviders } from '../msal'

import { Link } from 'react-router-dom'
import { MsalStatus } from '../msal/msal-status'
import clsx from 'clsx'
import { useAuth } from '../core/Auth'
import { useFormik } from 'formik'
import { useLoginMutation } from '../../../framework/services/auth'
import { useState } from 'react'
import { toAbsoluteUrl } from '../../../helpers'

const loginSchema = Yup.object().shape({
  userName: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('User name is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues = {
  userName: '',
  password: '',
}

export function Login() {
  const [loading, setLoading] = useState(false)
  const { saveAuth, setUser } = useAuth()
  const [login] = useLoginMutation()
  const { authTypes } = useAuthProviders()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      setSubmitting(true)

      login({ userName: values.userName, password: values.password })
        .unwrap()
        .then((result) => {
          saveAuth(result.auth)
          setUser(result.user)
        })
        .catch(err => {
          saveAuth(undefined)
          setStatus(err.data.message || 'An error occurred while processing your request. Please try again later.')
        })
        .finally(() => {
          setLoading(false);
          setSubmitting(false)
        })
    },
  })

  if (MsalStatus.isInProgress()) {
    return <div className='d-flex flex-column align-items-center justify-content-center h-100 w-100'>
      <SignInButton className='btn-sm w-250px' />
    </div>
  }

  return (
    <div
      className='auth-card-responsive card bg-transparent'>
      <div className="w-100 mw-450px mx-auto px-3" style={{ maxWidth: '90%', minWidth: '320px' }}>
        <div
          className='card'
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            border: 'none',
          }}
        >
          <div className='card-header flex-column'>
            <h1 className='text-dark mt-5'>Sign In</h1>
            <div className='text-gray-800 fw-semibold fs-7'>
              New Here?{' '}
              <Link to='/auth/registration' className='link-primary fw-bolder'>
                Create an Account
              </Link>
            </div>
          </div>
          <div className="card-body">
            {(authTypes.indexOf('MSAL') !== -1) && (
              <>
                <div className='text-center mt-0'>
                  <h6 className='text-dark my-2 fs-4'>Sign in as Student</h6>
                </div>
                <div className="mt-4 text-center">
                  <SignInButton className="btn-sm w-100" />
                </div>
              </>
            )}
            <div className='d-none'>
              <div className='text-center mt-12 mb-2'>
                <h6 className='text-dark my-2 fs-4 '>Sign in as Parent</h6>
              </div>
              <form
                className="form fv-plugins-bootstrap5 fv-plugins-framework mt-4"
                onSubmit={formik.handleSubmit}
                noValidate
                id="kt_login_signin_form"
              >
                {formik.status && (
                  <div className="alert alert-danger">
                    <div className="alert-text font-weight-bold">{formik.status}</div>
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label fw-bolder text-dark">User Name</label>
                  <input
                    placeholder="User Name"
                    {...formik.getFieldProps('userName')}
                    className={clsx('form-control', {
                      'is-invalid': formik.touched.userName && formik.errors.userName,
                      'is-valid': formik.touched.userName && !formik.errors.userName,
                    })}
                    type="text"
                    name="userName"
                    autoComplete="off"
                  />
                  <FieldError show={formik.touched.userName && !!formik.errors.userName} message={formik.errors.userName} />
                </div>

                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <label className="form-label fw-bolder text-dark">Password</label>
                    <Link to="/auth/forgot-password" className="link-primary fw-bold">
                      Forgot Password ?
                    </Link>
                  </div>
                  <input
                    type="password"
                    autoComplete="off"
                    {...formik.getFieldProps('password')}
                    className={clsx(
                      'form-control', {
                      'is-invalid': formik.touched.password && formik.errors.password,
                      'is-valid': formik.touched.password && !formik.errors.password,
                    })}
                  />
                  <FieldError show={formik.touched.password && !!formik.errors.password} message={formik.errors.password} />
                </div>

                <div className="text-center mt-4">
                  <button
                    type="submit"
                    id="kt_sign_in_submit"
                    className="btn btn-lg btn-primary w-100"
                    disabled={formik.isSubmitting || !formik.isValid}
                  >
                    {!loading && <span className="indicator-label">Continue</span>}
                    {loading && (
                      <span className="indicator-progress">
                        Please wait...
                        <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                      </span>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const FieldError = ({ show, message }: { show?: boolean, message?: string }) => {
  if (!show) return <></>

  return <div className='fv-plugins-message-container'>
    <div className='fv-help-block fs-8'>
      <span role='alert'>{message}</span>
    </div>
  </div>
}